async function get(url: string, input: Record<string, string>, headers: Record<string, string>) {
    if (!input || Object.entries(input).length === 0) {
        return await fetch(url, { headers });
    }
    return await fetch(`${url}?${new URLSearchParams(input).toString()}`, { headers });
}

async function post(url: string, input: Record<string, any>, headers: Record<string, string>) {
    return await fetch(url, {
        method: "POST",
        body: JSON.stringify(input),
        headers: { ...headers, "Content-Type": "application/json" },
    });
}

type APIResponseType<T> = {
    success: boolean;
    message: string;
    data: T;
};

export class APIFunction {
    static async getThemeMetadata(nanoId: string): Promise<
        APIResponseType<{
            name: string;
            nanoId: string;
            showNavignationButtons: string;
            status: string;
            lineCardDisplay: string;
        }>
    > {
        const url = `${process.env.NEXT_PUBLIC_API_DOMAIN}/themes/${nanoId}`;
        const res = await get(url, {}, {});
        return res.json();
    }

    static async getActiveTheme(shopifyDomain: string): Promise<APIResponseType<{ nanoId: string }>> {
        const url = `${process.env.NEXT_PUBLIC_API_DOMAIN}/themes/activeTheme`;
        const res = await get(url, { shopifyDomain }, {});
        return res.json();
    }

    static async getThemeComponentsForPage(
        nanoId: string,
        shopifyDomain: string,
        pageKey: string,
        locale: string,
    ): Promise<
        APIResponseType<
            {
                componentKey: string;
                componentType: "STANDARD" | "CUSTOM";
                body: string | null;
                defaultPosition: number;
                defaultWidth: number;
                componentId: number;
                position: number;
                width: number;
            }[]
        >
    > {
        const url = `${process.env.NEXT_PUBLIC_API_DOMAIN}/themes/${nanoId}/components`;
        const res = await get(url, { shopifyDomain, pageKey, locale }, {});
        return res.json();
    }

    static async performSessionTokenAuthCheck(sessionToken: string): Promise<{
        code: string;
        message: string;
        data: {
            accessToken: string;
            refreshToken: string;
        };
        success: boolean;
    }> {
        const url = `${process.env.NEXT_PUBLIC_AUTH_SERVICE_DOMAIN}/storefront/refreshToken`;
        const res = await post(url, { sessionToken }, {});
        return res.json();
    }

    static async verifyCustomerToken(token: string): Promise<{
        success: boolean;
        message: string;
        data: {
            customerShopifyId: number;
            customerFirstName: string;
            customerLastName: string;
            customerEmail: string;
            customerTags: string[];
            total_orders_placed_by_customer: number | null;
            total_spent_by_customer: string;
            total_subscriptions_orders_by_customer: number;
            total_subscriptions_spent_by_customer: string;
        };
        messageKey: string;
        placeholders: string[] | null;
    }> {
        const url = `${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/customer/v2/verify_token`;
        const res = await get(url, {}, { Authorization: `Bearer ${token}` });
        return res.json();
    }

    static async getAllTranslations(
        shopifyDomain: string,
        previewThemeId: string | null,
        locale: string,
    ): Promise<
        APIResponseType<{
            translations: { [key: string]: string };
            entities: { [key: string]: string };
        }>
    > {
        const url = `${process.env.NEXT_PUBLIC_API_DOMAIN}/translations/all`;
        const input: any = { shopifyDomain };
        if (locale) {
            input.locale = locale;
        }
        if (previewThemeId) {
            input.previewThemeId = previewThemeId;
        }
        const res = await get(url, input, {});
        return res.json();
    }

    static async getTranslationsByType(
        shopifyDomain: string,
        locale: string,
        types: string,
    ): Promise<
        APIResponseType<{
            [key: string]: any;
        }>
    > {
        const url = `${process.env.NEXT_PUBLIC_API_DOMAIN}/translations`;
        const input: any = { shopifyDomain, types };
        if (locale) {
            input.locale = locale;
        }
        const res = await get(url, input, {});
        return res.json();
    }

    static async getCancellationOffersShortDescription(
        shopifyDomain: string,
        locale: string | null,
    ): Promise<
        APIResponseType<{
            [key: string]: any;
        }>
    > {
        const url = `${process.env.NEXT_PUBLIC_API_DOMAIN}/translations/cancellationOffersShortDescription`;
        const input: any = { shopifyDomain };
        if (locale) {
            input.locale = locale;
        }
        const res = await get(url, input, {});
        return res.json();
    }

    static async assessMysteryRewards(
        shopifyDomain: string,
        subscriptionContractShopifyId: string,
        locale: string,
    ): Promise<any | null> {
        const url = `${process.env.NEXT_PUBLIC_API_DOMAIN}/mysteryReward/assess`;
        const input: any = { shopifyDomain, subscriptionContractShopifyId, locale };
        const res = await get(url, input, {});
        return res.json();
    }

    static async clickMysteryReward(
        shopifyDomain: string,
        subscriptionContractShopifyId: number,
        actionType: string,
        rewardNanoId: string,
    ) {
        const url = `${process.env.NEXT_PUBLIC_API_DOMAIN}/mysteryReward/click`;
        const input: any = { shopifyDomain, subscriptionContractShopifyId, actionType, rewardNanoId };
        const res = await post(url, input, {});
        return res.json();
    }

    static async viewMysteryReward(
        shopifyDomain: string,
        subscriptionContractShopifyId: number,
        rewardNanoId: string,
        viewChannel: "CUSTOMER_PORTAL" | "CAMPAIGN_ACTION_URL",
    ) {
        const url = `${process.env.NEXT_PUBLIC_API_DOMAIN}/mysteryReward/view`;
        const input: any = { shopifyDomain, subscriptionContractShopifyId, rewardNanoId, viewChannel };
        const res = await post(url, input, {});
        return res.json();
    }

    static async assessCampaignBanner(
        shopifyDomain: string,
        customerShopifyId: number,
        subscriptionContractShopifyId: string,
        locale: string,
    ): Promise<any | null> {
        const url = `${process.env.NEXT_PUBLIC_API_DOMAIN}/campaigns/assess`;
        const input: any = { shopifyDomain, subscriptionContractShopifyId, locale, customerShopifyId };
        const res = await get(url, input, {});
        return res.json();
    }

    static async clickCampaign(quickActionUUID: string, shopifyDomain: string, customerShopifyId: number) {
        const url = `${process.env.NEXT_PUBLIC_API_DOMAIN}/campaigns/click`;
        const input: any = { shopifyDomain, quickActionUUID, customerShopifyId };
        const res = await post(url, input, {});
        return res.json();
    }
}
